// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
//  customer/src/pages/Checkout.jsx  —  Quickry
//  Zomato-style address UI + bill summary
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
import { useState } from "react";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import {
  MapPin, LocateFixed, ArrowLeft, Home as HomeIcon,
  Building2, MapPinned, ChevronRight, Check, Pencil,
  Loader2, X, Plus,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

const T = {
  primary:"#f97316", primaryLight:"#fff7ed", primaryMid:"#fed7aa", primaryDark:"#ea580c",
  bg:"#f2f2f7", white:"#ffffff", text:"#1c1c1e", textSub:"#6b7280", textMuted:"#9ca3af",
  border:"#e5e7eb", green:"#16a34a", greenLight:"#dcfce7", red:"#ef4444",
};

const ADDRESS_TYPES = [
  { key:"Home",  icon:HomeIcon,   label:"Home"  },
  { key:"Work",  icon:Building2,  label:"Work"  },
  { key:"Other", icon:MapPinned,  label:"Other" },
];

export default function Checkout() {
  const { cart } = useCart();
  const { user, getToken } = useAuth();
  const navigate = useNavigate();

  // Address form state
  const [addrType,    setAddrType]    = useState("Home");
  const [flatNo,      setFlatNo]      = useState("");
  const [area,        setArea]        = useState("");
  const [landmark,    setLandmark]    = useState("");
  const [mobile,      setMobile]      = useState(user?.phone || "");
  const [locLoading,  setLocLoading]  = useState(false);
  const [confirmed,   setConfirmed]   = useState(false);
  const [editing,     setEditing]     = useState(true);
  const [errors,      setErrors]      = useState({});

  // Bill
  const itemTotal  = cart.reduce((s, i) => s + i.price * i.qty, 0);
  const deliveryFee = itemTotal >= 299 ? 0 : 30;
  const platformFee = 5;
  const gst         = Math.round(itemTotal * 0.05);
  const total       = itemTotal + deliveryFee + platformFee + gst;

  // Get current location via GPS
  const getLocation = () => {
    if (!navigator.geolocation) { alert("Location not supported"); return; }
    setLocLoading(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const res  = await fetch(
            `https://nominatim.openstreetmap.org/reverse?lat=${pos.coords.latitude}&lon=${pos.coords.longitude}&format=json`
          );
          const data = await res.json();
          const a    = data.address || {};
          // Fill area from GPS
          const areaStr = [a.road, a.suburb, a.neighbourhood].filter(Boolean).join(", ");
          setArea(areaStr || data.display_name?.split(",").slice(0, 2).join(", ") || "");
        } catch (e) {
          alert("Could not fetch address. Please type manually.");
        }
        setLocLoading(false);
      },
      () => { alert("Location permission denied"); setLocLoading(false); }
    );
  };

  // Validate & confirm address
  const confirmAddress = () => {
    const e = {};
    if (!flatNo.trim())                              e.flatNo = "House/Flat no. required";
    if (!area.trim())                                e.area   = "Area/Street required";
    if (!mobile.trim())                              e.mobile = "Mobile number is required";
    else if (!/^[6-9]\d{9}$/.test(mobile.trim()))   e.mobile = "Enter a valid 10-digit mobile number";
    if (Object.keys(e).length) { setErrors(e); return; }
    setErrors({});
    setConfirmed(true);
    setEditing(false);
  };

  const fullAddress = [flatNo, area, landmark, "Akola"].filter(Boolean).join(", ");

  return (
    <div style={{ background:T.bg, minHeight:"100vh", fontFamily:"'Plus Jakarta Sans','Outfit',sans-serif", color:T.text, paddingBottom:100 }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,900&family=Plus+Jakarta+Sans:wght@400;600;700;800&display=swap');
        *,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
        input,button,textarea{font-family:inherit;outline:none}
        @keyframes spin{to{transform:rotate(360deg)}}
        .spin{animation:spin .75s linear infinite}
        input:focus{border-color:${T.primary}!important;box-shadow:0 0 0 3px rgba(249,115,22,0.08)!important}
        textarea:focus{border-color:${T.primary}!important;box-shadow:0 0 0 3px rgba(249,115,22,0.08)!important}
      `}</style>

      {/* Header */}
      <div style={{ position:"sticky", top:0, zIndex:100, background:T.white, borderBottom:`1px solid ${T.border}`, padding:"13px 16px" }}>
        <div style={{ display:"flex", alignItems:"center", gap:12, maxWidth:480, margin:"0 auto" }}>
          <button onClick={() => navigate(-1)} style={{ background:"none", border:"none", cursor:"pointer", color:T.text, display:"flex" }}>
            <ArrowLeft size={21}/>
          </button>
          <div style={{ fontFamily:"'Fraunces',Georgia,serif", fontWeight:900, fontSize:"1.1rem", letterSpacing:"-0.5px" }}>
            Checkout
          </div>
        </div>
      </div>

      <div style={{ maxWidth:480, margin:"0 auto", padding:"14px 16px" }}>

        {/* ── DELIVERY ADDRESS CARD ── */}
        <div style={{ background:T.white, borderRadius:16, border:`1px solid ${T.border}`, marginBottom:12, overflow:"hidden", boxShadow:"0 1px 8px rgba(0,0,0,0.05)" }}>

          {/* Card Header */}
          <div style={{ padding:"14px 16px 0", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
            <div style={{ display:"flex", alignItems:"center", gap:8 }}>
              <div style={{ width:34, height:34, borderRadius:10, background:T.primaryLight, display:"flex", alignItems:"center", justifyContent:"center" }}>
                <MapPin size={17} color={T.primary}/>
              </div>
              <div>
                <div style={{ fontWeight:800, fontSize:"0.9rem", color:T.text }}>Delivery Address</div>
                <div style={{ fontSize:"0.7rem", color:T.textSub }}>Where should we deliver?</div>
              </div>
            </div>
            {confirmed && !editing && (
              <button onClick={() => setEditing(true)}
                style={{ background:"none", border:`1px solid ${T.primary}`, color:T.primary, borderRadius:8, padding:"5px 10px", fontWeight:700, fontSize:"0.72rem", cursor:"pointer", display:"flex", alignItems:"center", gap:4 }}>
                <Pencil size={11}/> Edit
              </button>
            )}
          </div>

          {/* Confirmed Address Preview */}
          {confirmed && !editing ? (
            <div style={{ padding:"12px 16px 16px" }}>
              <div style={{ display:"flex", alignItems:"flex-start", gap:10, background:T.primaryLight, border:`1px solid ${T.primaryMid}`, borderRadius:12, padding:"12px 14px" }}>
                <Check size={16} color={T.green} style={{ marginTop:2, flexShrink:0 }}/>
                <div>
                  <div style={{ fontWeight:700, fontSize:"0.78rem", color:T.primary, marginBottom:2 }}>{addrType}</div>
                  <div style={{ fontSize:"0.84rem", color:T.text, lineHeight:1.5 }}>{fullAddress}</div>
                </div>
              </div>
            </div>
          ) : (
            <div style={{ padding:"14px 16px 18px" }}>

              {/* Address Type Selector */}
              <div style={{ marginBottom:16 }}>
                <div style={{ fontSize:"0.68rem", fontWeight:700, color:T.textSub, letterSpacing:"0.5px", marginBottom:8, textTransform:"uppercase" }}>Save As</div>
                <div style={{ display:"flex", gap:8 }}>
                  {ADDRESS_TYPES.map(({ key, icon:Icon, label }) => {
                    const sel = addrType === key;
                    return (
                      <button key={key} onClick={() => setAddrType(key)}
                        style={{ flex:1, display:"flex", flexDirection:"column", alignItems:"center", gap:5, padding:"10px 8px", borderRadius:12, border:`1.5px solid ${sel ? T.primary : T.border}`, background:sel ? T.primaryLight : T.white, cursor:"pointer", transition:"all .2s" }}>
                        <Icon size={18} color={sel ? T.primary : T.textMuted} strokeWidth={sel ? 2.2 : 1.8}/>
                        <span style={{ fontSize:"0.72rem", fontWeight:sel ? 700 : 600, color:sel ? T.primary : T.textSub }}>{label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* GPS Button */}
              <button onClick={getLocation} disabled={locLoading}
                style={{ width:"100%", background:T.greenLight, border:`1px solid #bbf7d0`, borderRadius:12, padding:"11px", display:"flex", alignItems:"center", justifyContent:"center", gap:8, color:T.green, fontWeight:700, fontSize:"0.83rem", cursor:"pointer", marginBottom:14, transition:"opacity .2s", opacity:locLoading ? 0.7 : 1 }}>
                {locLoading
                  ? <><Loader2 size={15} className="spin"/> Fetching your location...</>
                  : <><LocateFixed size={15}/> Use Current Location</>
                }
              </button>

              {/* Flat / House No */}
              <div style={{ marginBottom:10 }}>
                <div style={{ fontSize:"0.68rem", fontWeight:700, color:T.textSub, letterSpacing:"0.5px", marginBottom:5, textTransform:"uppercase" }}>
                  House / Flat / Floor No. *
                </div>
                <input
                  type="text"
                  placeholder="e.g. Flat 302, Shree Apartments"
                  value={flatNo}
                  onChange={e => { setFlatNo(e.target.value); setErrors(p => ({...p, flatNo:""})); }}
                  style={{ width:"100%", border:`1.5px solid ${errors.flatNo ? T.red : T.border}`, borderRadius:10, padding:"11px 14px", fontSize:"0.88rem", color:T.text, background:T.bg, transition:"border-color .2s" }}
                />
                {errors.flatNo && <div style={{ color:T.red, fontSize:"0.7rem", marginTop:4, fontWeight:600 }}>{errors.flatNo}</div>}
              </div>

              {/* Area / Street */}
              <div style={{ marginBottom:10 }}>
                <div style={{ fontSize:"0.68rem", fontWeight:700, color:T.textSub, letterSpacing:"0.5px", marginBottom:5, textTransform:"uppercase" }}>
                  Area / Street *
                </div>
                <input
                  type="text"
                  placeholder="e.g. Civil Lines, MG Road, Akola"
                  value={area}
                  onChange={e => { setArea(e.target.value); setErrors(p => ({...p, area:""})); }}
                  style={{ width:"100%", border:`1.5px solid ${errors.area ? T.red : T.border}`, borderRadius:10, padding:"11px 14px", fontSize:"0.88rem", color:T.text, background:T.bg, transition:"border-color .2s" }}
                />
                {errors.area && <div style={{ color:T.red, fontSize:"0.7rem", marginTop:4, fontWeight:600 }}>{errors.area}</div>}
              </div>

              {/* Landmark */}
              <div style={{ marginBottom:10 }}>
                <div style={{ fontSize:"0.68rem", fontWeight:700, color:T.textSub, letterSpacing:"0.5px", marginBottom:5, textTransform:"uppercase" }}>
                  Nearby Landmark (Optional)
                </div>
                <input
                  type="text"
                  placeholder="e.g. Near SBI Bank, Opposite Bus Stand"
                  value={landmark}
                  onChange={e => setLandmark(e.target.value)}
                  style={{ width:"100%", border:`1.5px solid ${T.border}`, borderRadius:10, padding:"11px 14px", fontSize:"0.88rem", color:T.text, background:T.bg, transition:"border-color .2s" }}
                />
              </div>

              {/* Mobile Number */}
              <div style={{ marginBottom:16 }}>
                <div style={{ fontSize:"0.68rem", fontWeight:700, color:T.textSub, letterSpacing:"0.5px", marginBottom:5, textTransform:"uppercase" }}>
                  Mobile Number *
                </div>
                <div style={{ position:"relative" }}>
                  <span style={{ position:"absolute", left:12, top:"50%", transform:"translateY(-50%)", fontSize:"0.88rem", color:T.textSub, fontWeight:600 }}>+91</span>
                  <input
                    type="tel"
                    placeholder="10-digit mobile number"
                    value={mobile}
                    maxLength={10}
                    onChange={e => { setMobile(e.target.value.replace(/\D/g,"")); setErrors(p => ({...p, mobile:""})); }}
                    style={{ width:"100%", border:`1.5px solid ${errors.mobile ? T.red : T.border}`, borderRadius:10, padding:"11px 14px 11px 42px", fontSize:"0.88rem", color:T.text, background:T.bg, transition:"border-color .2s" }}
                  />
                </div>
                {errors.mobile && <div style={{ color:T.red, fontSize:"0.7rem", marginTop:4, fontWeight:600 }}>{errors.mobile}</div>}
                <div style={{ fontSize:"0.68rem", color:T.textMuted, marginTop:4 }}>
                  Delivery partner will call on this number
                </div>
              </div>

              {/* Confirm Button */}
              <button onClick={confirmAddress}
                style={{ width:"100%", background:`linear-gradient(135deg,${T.primaryDark},${T.primary})`, color:"#fff", border:"none", borderRadius:12, padding:"13px", fontWeight:800, fontSize:"0.92rem", cursor:"pointer", boxShadow:"0 5px 18px rgba(249,115,22,0.25)" }}>
                Confirm Delivery Address
              </button>
            </div>
          )}
        </div>

        {/* ── ORDER SUMMARY ── */}
        <div style={{ background:T.white, borderRadius:16, border:`1px solid ${T.border}`, marginBottom:12, boxShadow:"0 1px 8px rgba(0,0,0,0.05)" }}>
          <div style={{ padding:"14px 16px 0", fontSize:"0.65rem", fontWeight:700, color:T.textMuted, letterSpacing:"1.5px", textTransform:"uppercase" }}>Order Items</div>
          <div style={{ padding:"10px 16px 14px" }}>
            {cart.length === 0
              ? <p style={{ color:T.textMuted, fontSize:"0.85rem" }}>No items in cart</p>
              : cart.map(item => (
                  <div key={item._id} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", fontSize:"0.85rem", marginBottom:10 }}>
                    <div style={{ flex:1 }}>
                      <div style={{ fontWeight:600, color:T.text }}>{item.name}</div>
                      <div style={{ fontSize:"0.75rem", color:T.textSub }}>× {item.qty}</div>
                    </div>
                    <div style={{ fontWeight:700, color:T.text }}>₹{item.price * item.qty}</div>
                  </div>
                ))
            }
          </div>
        </div>

        {/* ── BILL DETAILS ── */}
        <div style={{ background:T.white, borderRadius:16, border:`1px solid ${T.border}`, marginBottom:12, boxShadow:"0 1px 8px rgba(0,0,0,0.05)" }}>
          <div style={{ padding:"14px 16px 0", fontSize:"0.65rem", fontWeight:700, color:T.textMuted, letterSpacing:"1.5px", textTransform:"uppercase" }}>Bill Details</div>
          <div style={{ padding:"12px 16px 14px" }}>
            {[
              { label:"Item Total",    val:`₹${itemTotal}`                               },
              { label:"Delivery Fee",  val:deliveryFee === 0 ? "FREE 🎉" : `₹${deliveryFee}`, green: deliveryFee === 0 },
              { label:"Platform Fee",  val:`₹${platformFee}`                             },
              { label:"GST (5%)",      val:`₹${gst}`                                     },
            ].map((r, i) => (
              <div key={i} style={{ display:"flex", justifyContent:"space-between", fontSize:"0.83rem", color:T.textSub, marginBottom:9 }}>
                <span>{r.label}</span>
                <span style={{ fontWeight:600, color: r.green ? T.green : T.text }}>{r.val}</span>
              </div>
            ))}
            <div style={{ height:1, background:T.border, margin:"10px 0" }}/>
            <div style={{ display:"flex", justifyContent:"space-between", fontWeight:800, fontSize:"1rem", color:T.text }}>
              <span>To Pay</span>
              <span style={{ color:T.primary }}>₹{total}</span>
            </div>
          </div>
        </div>

      </div>

      {/* ── STICKY PLACE ORDER ── */}
      <div style={{ position:"fixed", bottom:0, left:0, right:0, background:T.white, borderTop:`1px solid ${T.border}`, padding:"12px 16px", boxShadow:"0 -4px 20px rgba(0,0,0,0.06)", zIndex:100 }}>
        <div style={{ maxWidth:480, margin:"0 auto" }}>
          <button
            onClick={() => {
              if (!confirmed) { confirmAddress(); return; }
              navigate("/cart");
            }}
            style={{ width:"100%", background:confirmed ? `linear-gradient(135deg,${T.primaryDark},${T.primary})` : T.border, color:confirmed ? "#fff" : T.textMuted, border:"none", borderRadius:14, padding:"15px", fontWeight:800, fontSize:"0.97rem", cursor:confirmed ? "pointer" : "not-allowed", display:"flex", alignItems:"center", justifyContent:"space-between", boxShadow:confirmed ? "0 6px 20px rgba(249,115,22,0.28)" : "none", transition:"all .25s" }}>
            <span>{confirmed ? "Place Order" : "Add Address to Continue"}</span>
            <span style={{ fontWeight:800 }}>₹{total} <ChevronRight size={16} style={{ display:"inline", verticalAlign:"middle" }}/></span>
          </button>
        </div>
      </div>
    </div>
  );
}